// Pages (TOM-618, a local change: see VENDORED.md). A panel can be a small
// page of HTML, CSS and SVG, never scripts, drawn in its card in a sealed
// frame with live readings fed in by the app. This file is the page's
// allowlist, pure data and pure helpers that load anywhere: the server parses
// a page fully against these lists before it is stored (TOM-619), and the
// drawer (page.js) walks the browser's own parse against them again before
// anything is inserted.
//
// Version 2 pages (cloud ADR 0393, TOM-665; VENDORED.md change 10) bind the
// app's figures through `data-tc-*` attributes and hold `<tc-live>` areas.
// The binding vocabulary, its values, the drawing cost caps, the CSS a
// version 2 page may not hold, and the arithmetic of history paths, ticks,
// day bands, marks and statistics are here, so the server's check, its
// renderer and the web draw them alike. The tick, band and curve helpers are
// HistoryChart's own (cloud/web/src/components/charts), moved here; the web
// chart imports them back.

/** The frame document's policy, its first element: nothing loads but inline style and data: images and fonts. */
export const PAGE_CSP =
  "default-src 'none'; style-src 'unsafe-inline'; img-src data:; font-src data:; base-uri 'none'; form-action 'none'";
/** The frame's sandbox: the host reads and fills the page, and nothing in it runs. Never add allow-scripts. */
export const PAGE_SANDBOX = "allow-same-origin";
/**
 * A page's html and css together, in UTF-8 bytes: the drawer's second lock at
 * a version 2 page's cap (TOM-665). The server enforces each version's own
 * (32 KB for version 1, 64 KB for version 2).
 */
export const PAGE_MAX_BYTES = 65536;
/** Face slots (`tc-face` and `tc-history`) a page may hold. */
export const PAGE_MAX_FACES = 6;
/** A reading's name on a page: `--depth`, `data-depth-zone`. */
export const READING_NAME = /^[a-z][a-z0-9-]{0,23}$/;
/** A composed view's slot name, as `data-slot` carries it (§5.6, TOM-665). */
const SLOT_NAME = /^[a-z][a-z0-9-]{0,39}$/;
/**
 * A `<tc-history>`'s windows: the ones the sparkline it draws takes (TOM-621,
 * a local change: see VENDORED.md). The source listed "6h" too, which no
 * history face draws.
 */
export const HISTORY_WINDOWS = Object.freeze(["1h", "24h", "7d", "30d"]);

const htmlElements = [
  "div",
  "span",
  "p",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "b",
  "i",
  "em",
  "strong",
  "small",
  "sub",
  "sup",
  "br",
  "hr",
  "ul",
  "ol",
  "li",
  "table",
  "caption",
  "colgroup",
  "col",
  "thead",
  "tbody",
  "tfoot",
  "tr",
  "th",
  "td",
  "figure",
  "figcaption",
  "section",
  "header",
  "footer",
  "article",
  "aside",
  "blockquote",
  "pre",
  "code",
  "mark",
  "abbr",
  "time",
  "img",
];
const svgElements = [
  "svg",
  "g",
  "defs",
  "symbol",
  "use",
  "path",
  "rect",
  "circle",
  "ellipse",
  "line",
  "polyline",
  "polygon",
  "text",
  "tspan",
  "textPath",
  "linearGradient",
  "radialGradient",
  "stop",
  "pattern",
  "clipPath",
  "mask",
  "marker",
  "filter",
  "feBlend",
  "feColorMatrix",
  "feComponentTransfer",
  "feComposite",
  "feConvolveMatrix",
  "feDiffuseLighting",
  "feDisplacementMap",
  "feDistantLight",
  "feDropShadow",
  "feFlood",
  "feFuncA",
  "feFuncB",
  "feFuncG",
  "feFuncR",
  "feGaussianBlur",
  "feImage",
  "feMerge",
  "feMergeNode",
  "feMorphology",
  "feOffset",
  "fePointLight",
  "feSpecularLighting",
  "feSpotLight",
  "feTile",
  "feTurbulence",
  "title",
  "desc",
  "animate",
  "animateTransform",
  "animateMotion",
  "set",
];
/**
 * The page's own tags, which the drawer fills with live readings and faces,
 * and a version 2 page's `<tc-live>` areas, which the app draws over.
 */
const pageTags = [
  "tc-value",
  "tc-unit",
  "tc-age",
  "tc-face",
  "tc-history",
  "tc-live",
];
export const pageElements = Object.freeze([
  ...htmlElements,
  ...svgElements,
  ...pageTags,
]);
export const refusedPageElements = Object.freeze([
  "script",
  "iframe",
  "frame",
  "object",
  "embed",
  "applet",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "link",
  "meta",
  "base",
  "style",
  "audio",
  "video",
  "source",
  "track",
  "foreignObject",
  "noscript",
  "template",
  "slot",
  "portal",
  "dialog",
]);

/** Attributes any element may carry, besides `aria-*` and `data-*`. */
const globalAttributes = ["class", "id", "style", "role", "lang", "dir"];
/** SVG presentation and geometry attributes, also allowed on any element. */
const svgAttributes = [
  "xmlns",
  "xmlns:xlink",
  "version",
  "viewBox",
  "preserveAspectRatio",
  "x",
  "y",
  "x1",
  "y1",
  "x2",
  "y2",
  "cx",
  "cy",
  "r",
  "rx",
  "ry",
  "fx",
  "fy",
  "fr",
  "width",
  "height",
  "d",
  "points",
  "pathLength",
  "transform",
  "transform-origin",
  "transform-box",
  "gradientTransform",
  "gradientUnits",
  "spreadMethod",
  "offset",
  "patternUnits",
  "patternContentUnits",
  "patternTransform",
  "clipPathUnits",
  "maskUnits",
  "maskContentUnits",
  "filterUnits",
  "primitiveUnits",
  "markerUnits",
  "markerWidth",
  "markerHeight",
  "refX",
  "refY",
  "orient",
  "dx",
  "dy",
  "rotate",
  "textLength",
  "lengthAdjust",
  "startOffset",
  "method",
  "spacing",
  "side",
  "fill",
  "fill-opacity",
  "fill-rule",
  "stroke",
  "stroke-width",
  "stroke-opacity",
  "stroke-linecap",
  "stroke-linejoin",
  "stroke-dasharray",
  "stroke-dashoffset",
  "stroke-miterlimit",
  "opacity",
  "color",
  "display",
  "visibility",
  "overflow",
  "clip-path",
  "clip-rule",
  "mask",
  "filter",
  "marker-start",
  "marker-mid",
  "marker-end",
  "stop-color",
  "stop-opacity",
  "flood-color",
  "flood-opacity",
  "lighting-color",
  "font-family",
  "font-size",
  "font-size-adjust",
  "font-stretch",
  "font-style",
  "font-variant",
  "font-weight",
  "letter-spacing",
  "word-spacing",
  "text-anchor",
  "text-decoration",
  "text-rendering",
  "dominant-baseline",
  "alignment-baseline",
  "baseline-shift",
  "writing-mode",
  "direction",
  "unicode-bidi",
  "paint-order",
  "vector-effect",
  "shape-rendering",
  "image-rendering",
  "color-interpolation",
  "color-interpolation-filters",
  "mix-blend-mode",
  "isolation",
  "pointer-events",
  // Filter primitives.
  "in",
  "in2",
  "result",
  "stdDeviation",
  "operator",
  "k1",
  "k2",
  "k3",
  "k4",
  "mode",
  "values",
  "type",
  "tableValues",
  "slope",
  "intercept",
  "amplitude",
  "exponent",
  "scale",
  "xChannelSelector",
  "yChannelSelector",
  "baseFrequency",
  "numOctaves",
  "seed",
  "stitchTiles",
  "radius",
  "order",
  "kernelMatrix",
  "kernelUnitLength",
  "divisor",
  "bias",
  "targetX",
  "targetY",
  "edgeMode",
  "preserveAlpha",
  "surfaceScale",
  "diffuseConstant",
  "specularConstant",
  "specularExponent",
  "azimuth",
  "elevation",
  "pointsAtX",
  "pointsAtY",
  "pointsAtZ",
  "limitingConeAngle",
  "z",
  // Animation.
  "attributeName",
  "attributeType",
  "begin",
  "dur",
  "end",
  "min",
  "max",
  "restart",
  "repeatCount",
  "repeatDur",
  "calcMode",
  "keyTimes",
  "keySplines",
  "keyPoints",
  "from",
  "to",
  "by",
  "additive",
  "accumulate",
  "path",
  // Fragment references only: see attributeAllowed.
  "href",
  "xlink:href",
];
const elementAttributes = {
  img: ["src", "alt"],
  ol: ["start", "reversed"],
  li: ["value"],
  col: ["span"],
  colgroup: ["span"],
  td: ["colspan", "rowspan", "headers"],
  th: ["colspan", "rowspan", "headers", "scope", "abbr"],
  time: ["datetime"],
  "tc-value": ["reading", "decimals"],
  "tc-unit": ["reading"],
  "tc-age": ["reading"],
  "tc-face": ["control", "readings", "look"],
  "tc-history": ["reading", "window"],
  // Its `id` is a global attribute.
  "tc-live": [],
};
export const pageAttributes = Object.freeze({
  global: Object.freeze([...globalAttributes, "aria-*", "data-*"]),
  svg: Object.freeze([...svgAttributes]),
  elements: Object.freeze(
    Object.fromEntries(
      Object.entries(elementAttributes).map(([tag, names]) => [
        tag,
        Object.freeze([...names]),
      ]),
    ),
  ),
});
/** Never allowed, on any element; nor is any `on*` handler. */
export const refusedPageAttributes = Object.freeze([
  "srcset",
  "action",
  "formaction",
  "background",
  "poster",
  "ping",
]);
/** Images a page may carry, as `data:` URLs in an `img` element's `src`. */
const imageSource = /^data:image\/(?:png|jpeg|gif|webp|svg\+xml)[;,]/;
const fragment = /^#[A-Za-z_][\w.:-]*$/;
/** Attributes an animation must never drive. */
const unanimated = ["href", "xlink:href", "style", "src"];

export const cssAtRules = Object.freeze([
  "keyframes",
  "property",
  "media",
  "supports",
  "container",
  "font-face",
  "layer",
]);
export const refusedCssAtRules = Object.freeze([
  "import",
  "namespace",
  "charset",
  "page",
]);

const lower = (list) => new Set(list.map((name) => name.toLowerCase()));
const hostAttributes = new Set([
  "data-zone",
  "data-quality",
  "data-state",
  "data-tone",
]);
const allowedElements = lower(pageElements),
  refusedElements = lower(refusedPageElements),
  anyElementAttributes = lower([...globalAttributes, ...svgAttributes]),
  refusedAttributes = lower(refusedPageAttributes),
  ownAttributes = new Map(
    Object.entries(elementAttributes).map(([tag, names]) => [
      tag,
      lower(names),
    ]),
  );

/**
 * True for an element a page may hold. Names compare without case, as an HTML
 * parse reports them. A version 2 page holds no picture: `img` and `feImage`
 * are refused (TOM-665).
 */
export function elementAllowed(name, options = {}) {
  const tag = String(name).toLowerCase();
  if (options?.version === 2 && (tag === "img" || tag === "feimage"))
    return false;
  return allowedElements.has(tag) && !refusedElements.has(tag);
}

/**
 * SVG's animation elements. One that holds an attribute the walk refuses is
 * dropped whole, never kept without it (TOM-587): an `<animate>` or `<set>`
 * whose `attributeName="href"` is taken away still holds its `to` or
 * `values`, an address that nothing loads but that has no place in a page.
 */
export const animationElements = Object.freeze([
  "animate",
  "animateMotion",
  "animateTransform",
  "set",
]);
const animations = lower(animationElements);

/**
 * True for an element the walk keeps once its attributes are checked: false
 * for an animation element holding any attribute `attributeAllowed`
 * refuses, which goes whole. `attributes` is a list of `{ name, value }`,
 * as a DOM element's `attributes` or a parser's are.
 */
export function elementKeptWhole(element, attributes = [], options = {}) {
  if (!animations.has(String(element).toLowerCase())) return true;
  for (const { name, value } of attributes)
    if (!attributeAllowed(element, name, value, options)) return false;
  return true;
}

/**
 * True for an attribute, with this value, that a page's element may keep. With
 * `{ version: 2 }` (TOM-665) every `data-tc-*` binding's value is checked
 * against its rule (pageBindingValueAllowed), the attributes the host sets
 * (PAGE_HOST_ATTRIBUTES) are refused, and so are `dir`, `start` and `value`
 * on a list, the `direction`, `unicode-bidi` and `writing-mode` attributes, a
 * negative `letter-spacing`, a `url()` to anything but a `#fragment`, and a
 * `style` that pageCssV2Refusals refuses (a host-set property among them:
 * `options.readingNames` adds each reading's own); a version 1 page is read
 * as before. A chat page (`results: true`, cloud ADR 0399, TOM-870) keeps
 * its `data-tc-result` bindings too.
 */
export function attributeAllowed(element, name, value = "", options = {}) {
  const tag = String(element).toLowerCase(),
    attribute = String(name).toLowerCase(),
    text = String(value),
    trimmed = text.trim();
  if (attribute.startsWith("on") || refusedAttributes.has(attribute))
    return false;
  if (options?.version === 2) {
    if (hostAttributes.has(attribute)) return false;
    if (attribute === "dir" || textDirectionAttributes.has(attribute))
      return false;
    if (
      (tag === "ol" || tag === "li") &&
      ["start", "value"].includes(attribute)
    )
      return false;
    if (attribute.startsWith("data-tc-"))
      return pageBindingValueAllowed(attribute, text, options);
    if (attribute === "letter-spacing" && /^-/.test(normalizeCSS(trimmed)))
      return false;
    if (!cssUrlsAreFragments(text)) return false;
    if (
      attribute === "style" &&
      pageCssV2Refusals(text, options.readingNames ?? []).length > 0
    )
      return false;
  }
  if (attribute === "style") return cssLooksSafe(text);
  // Presentation attributes are CSS values too: fill="url(…)", filter, mask.
  if (
    !cssValueLooksSafe(normalizeCSS(text)) ||
    !cssValueLooksSafe(normalizeCSS(text, true))
  )
    return false;
  if (attribute === "href" || attribute === "xlink:href")
    return fragment.test(trimmed);
  if (attribute === "src")
    return tag === "img" && imageSource.test(trimmed.toLowerCase());
  if (attribute === "attributename") {
    const target = trimmed.toLowerCase();
    return !unanimated.includes(target) && !target.startsWith("on");
  }
  if (ownAttributes.get(tag)?.has(attribute))
    return tagValueAllowed(tag, attribute, trimmed);
  if (/^(?:aria|data)-[a-z0-9_.:-]+$/.test(attribute)) return true;
  return anyElementAttributes.has(attribute);
}

function tagValueAllowed(tag, attribute, value) {
  if (!tag.startsWith("tc-")) return true;
  if (attribute === "reading") return READING_NAME.test(value);
  if (attribute === "decimals") return /^[0-3]$/.test(value);
  if (attribute === "window") return HISTORY_WINDOWS.includes(value);
  if (attribute === "control") return /^[a-z][a-z0-9-]{0,39}$/.test(value);
  if (attribute === "readings") {
    const pairs = value.split(/\s+/).filter(Boolean);
    return (
      pairs.length > 0 &&
      pairs.length <= 8 &&
      pairs.every((pair) => {
        const [role, name, extra] = pair.split(":");
        return (
          extra === undefined &&
          /^[A-Za-z][A-Za-z0-9]{0,31}$/.test(role ?? "") &&
          READING_NAME.test(name ?? "")
        );
      })
    );
  }
  if (attribute === "look") {
    try {
      const look = JSON.parse(value);
      return look !== null && typeof look === "object" && !Array.isArray(look);
    } catch {
      return false;
    }
  }
  return false;
}

/**
 * CSS as a parser reads it, for matching: comments removed, escapes decoded,
 * lower case. `keepComments` leaves comments in, so a check can run on both
 * forms: a comment inside a string is not a comment to the parser.
 */
function normalizeCSS(css, keepComments = false) {
  const replacement = String.fromCodePoint(0xfffd);
  let out = String(css ?? "");
  if (!keepComments) out = out.replace(/\/\*[\s\S]*?(?:\*\/|$)/g, "");
  out = out.replace(
    /\\(?:([0-9a-fA-F]{1,6})(?:\r\n|[ \t\r\n\f])?|(\r\n|[\r\n\f])|([\s\S])|$)/g,
    (match, hex, newline, other) => {
      if (hex) {
        const code = parseInt(hex, 16);
        return code === 0 ||
          code > 0x10ffff ||
          (code >= 0xd800 && code < 0xe000)
          ? replacement
          : String.fromCodePoint(code);
      }
      if (newline) return "";
      return other ?? replacement;
    },
  );
  return out.toLowerCase();
}

/** The attributes that change the direction text is written in (contract §9.2). */
const textDirectionAttributes = new Set([
  "direction",
  "unicode-bidi",
  "writing-mode",
]);

/**
 * True when every `url()` in a CSS value points at a `#fragment` of the page:
 * a version 2 page holds no picture (contract §9.2, TOM-665).
 */
function cssUrlsAreFragments(css) {
  for (const form of [normalizeCSS(css), normalizeCSS(css, true)])
    for (const [, address] of form.matchAll(/url\s*\(\s*['"]?([^'")\s]*)/g))
      if (!address.startsWith("#")) return false;
  return true;
}

/** The value checks, on an already normalized string. */
function cssValueLooksSafe(css) {
  if (
    /(?:java|vb)script\s*:|expression\s*\(|-moz-binding|behavior\s*:|image-set\s*\(|(?:^|[^a-z0-9_-])src\s*\(/.test(
      css,
    )
  )
    return false;
  for (const [, address] of css.matchAll(/url\s*\(\s*['"]?([^'")\s]*)/g))
    if (!address.startsWith("#") && !/^data:(?:image|font)\//.test(address))
      return false;
  for (const [, argument] of css.matchAll(/attr\s*\(([^)]*)/g))
    if (/url|type\s*\(|</.test(argument)) return false;
  return true;
}

/**
 * A conservative string-level check of a page's CSS, or a style attribute:
 * the browser's second lock behind the server's full parse (TOM-619). It
 * refuses `@import` and every at-rule outside cssAtRules, `url()` to anything
 * but a `data:` image or font or a `#fragment` in the page, `image-set()`,
 * `src()`, `attr()` building a URL, `expression(` and `-moz-binding`, after
 * decoding CSS escapes, ignoring case, and both with and without comments.
 */
export function cssLooksSafe(css) {
  for (const form of [normalizeCSS(css), normalizeCSS(css, true)]) {
    if (!cssValueLooksSafe(form)) return false;
    for (const [, rule] of form.matchAll(/@([a-z0-9_-]*)/g))
      if (!cssAtRules.includes(rule)) return false;
  }
  return true;
}

/** True when a page's html and css fit PAGE_MAX_BYTES together. */
export function pageWithinLimits({ html = "", css = "" } = {}) {
  return (
    new TextEncoder().encode(String(html)).length +
      new TextEncoder().encode(String(css)).length <=
    PAGE_MAX_BYTES
  );
}

const numeric = (v) => typeof v === "number" && Number.isFinite(v);
const own = (record, key) =>
  record && Object.hasOwn(record, key) ? record[key] : undefined;

/** Where a value sits on a range, 0 to 1, clamped; null without a value or a usable range. */
export function readingFraction(value, range) {
  if (!numeric(value) || !Array.isArray(range)) return null;
  const [min, max] = range;
  if (!numeric(min) || !numeric(max) || min === max) return null;
  return Math.max(0, Math.min(1, (value - min) / (max - min)));
}

/** The first zone whose `below` or `above` holds for a value, by name; null when none does. */
export function readingZone(value, zones) {
  if (!numeric(value) || !Array.isArray(zones)) return null;
  for (const zone of zones)
    if (
      typeof zone?.name === "string" &&
      ((numeric(zone.below) && value < zone.below) ||
        (numeric(zone.above) && value > zone.above))
    )
      return zone.name;
  return null;
}

/** A `tc-value`'s text: the reading's own text, or its value to `decimals` places, or "—". */
export function pageValueText(reading, decimals) {
  if (!reading || reading.quality === "missing") return "—";
  const places = Number(decimals);
  if (
    decimals !== null &&
    decimals !== undefined &&
    decimals !== "" &&
    Number.isInteger(places) &&
    places >= 0 &&
    places <= 3 &&
    numeric(reading.value)
  ) {
    const text = reading.value.toFixed(places);
    return /^-0(?:\.0+)?$/.test(text) ? text.slice(1) : text;
  }
  if (typeof reading.text === "string" && reading.text) return reading.text;
  return numeric(reading.value) ? String(reading.value) : "—";
}

/** A `tc-age`'s text: "12 s ago", "4 min ago", "3 h ago", "2 d ago", or "—". */
export function pageAgeText(measuredAt, now) {
  if (!numeric(measuredAt) || !numeric(now)) return "—";
  const seconds = Math.max(0, Math.floor((now - measuredAt) / 1000));
  if (seconds < 60) return `${seconds} s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return `${Math.floor(seconds / 86400)} d ago`;
}

/**
 * What the drawer sets on a page's `<html>` for these readings: for each
 * reading `name`, the custom properties `--name` (its display value) and
 * `--name-fraction` (its store value on the page's `range`), and the
 * attributes `data-name-zone` (the first of the page's `zones` that holds, in
 * store units) and `data-name-quality`; and `data-theme`, `data-size`,
 * `data-expanded` (in the expand panel) and `data-slot` (the composed view's
 * slot a module page is drawn in, §5.6; TOM-665). A null means the drawer
 * removes it.
 */
export function pageVariables(page = {}, data = {}) {
  const properties = {},
    attributes = {
      "data-theme": ["light", "dark", "night"].includes(data.theme)
        ? data.theme
        : "dark",
      "data-size": ["s", "m", "l"].includes(data.size) ? data.size : "m",
      "data-expanded": data.expanded === true ? "" : null,
      "data-slot":
        typeof data.slot === "string" && SLOT_NAME.test(data.slot)
          ? data.slot
          : null,
    };
  const names = new Set([
    ...Object.keys(page.readings ?? {}),
    ...Object.keys(data.readings ?? {}),
  ]);
  for (const name of names) {
    if (!READING_NAME.test(name)) continue;
    const reading = own(data.readings, name),
      spec = own(page.readings, name) ?? {},
      quality = ["good", "stale", "missing"].includes(reading?.quality)
        ? reading.quality
        : "missing",
      shown = quality !== "missing",
      store = shown && numeric(reading.storeValue) ? reading.storeValue : null,
      fraction = store === null ? null : readingFraction(store, spec.range);
    properties[`--${name}`] =
      shown && numeric(reading.value) ? String(reading.value) : null;
    properties[`--${name}-fraction`] =
      fraction === null ? null : String(fraction);
    attributes[`data-${name}-zone`] =
      store === null ? null : readingZone(store, spec.zones);
    attributes[`data-${name}-quality`] = quality;
  }
  return { properties, attributes };
}

// ----- Version 2 pages (cloud ADR 0393, TOM-665; VENDORED.md change 10) -----

/** Every `data-tc-*` binding a version 2 page may carry (contract §4.2). */
export const PAGE_BINDING_ATTRIBUTES = Object.freeze([
  "data-tc-text",
  "data-tc-unit",
  "data-tc-age",
  "data-tc-frac",
  "data-tc-path",
  "data-tc-window",
  "data-tc-feed",
  "data-tc-status",
  "data-tc-field",
  "data-tc-slot",
  "data-tc-decimals",
  "data-tc-stat",
  "data-tc-age-style",
  "data-tc-name",
  "data-tc-equipment",
  "data-tc-device",
  "data-tc-state",
  "data-tc-points",
  "data-tc-shape",
  "data-tc-curve",
  "data-tc-group",
  "data-tc-box",
  "data-tc-fit",
  "data-tc-inset",
  "data-tc-y",
  "data-tc-scale",
  "data-tc-tick",
  "data-tc-time-tick",
  "data-tc-window-text",
  "data-tc-mark",
  "data-tc-at",
  "data-tc-day-band",
  "data-tc-freshness",
]);
/**
 * The states the host sets as `data-state` on a `data-tc-freshness` line
 * (TOM-989): live, past its dim time, past its dash time, never read.
 */
export const PAGE_FRESHNESS_STATES = Object.freeze([
  "live",
  "dim",
  "dash",
  "none",
]);
/** The core bindings (Q12), which Neo's catalog teaches first. */
export const PAGE_CORE_BINDINGS = Object.freeze([
  "data-tc-text",
  "data-tc-unit",
  "data-tc-age",
  "data-tc-frac",
  "data-tc-path",
  "data-tc-window",
  "data-tc-feed",
  "data-tc-status",
  "data-tc-field",
  "data-tc-slot",
]);
/**
 * The bindings only a chat page may carry (cloud ADR 0399, TOM-870): one of
 * the answer's `calculate` results, `data-tc-result="r1"`, its value and unit
 * as one text. The walk keeps one only with `{ version: 2, results: true }`,
 * so a board page never holds one; it is no part of PAGE_BINDING_ATTRIBUTES,
 * the board editor's catalog.
 */
export const PAGE_CHAT_BINDING_ATTRIBUTES = Object.freeze(["data-tc-result"]);
/** A bound result's id, as the answer's record gives it (r1–r8). */
export const PAGE_RESULT_ID = /^r[1-8]$/;
/** The attributes the host sets; a version 2 page never writes them. */
export const PAGE_HOST_ATTRIBUTES = Object.freeze([
  "data-zone",
  "data-quality",
  "data-state",
  "data-tone",
]);
/**
 * The custom properties the host sets, besides each reading's `--<name>` and
 * `--<name>-fraction` (pageHostProperty): a page's CSS never sets or registers
 * one.
 */
export const PAGE_HOST_PROPERTIES = /^--(?:frac|x|y|x0|x1|tc-.+)$/;
/** At most this many `<tc-live>` areas on one page. */
export const PAGE_MAX_LIVE = 2;
// Drawing cost caps (contract §9.3): the server refuses past them; the drawer
// inserts nothing past PAGE_MAX_ELEMENTS or PAGE_MAX_NODES.
export const PAGE_MAX_ELEMENTS = 1500;
/** Elements with every `<use>` expanded. */
export const PAGE_MAX_NODES = 5000;
export const PAGE_MAX_USE = 200;
export const PAGE_MAX_USE_DEPTH = 3;
export const PAGE_MAX_FILTERS = 8;
export const PAGE_MAX_FILTER_PRIMITIVES = 8;
export const PAGE_MAX_CSS_FILTERS = 12;
export const PAGE_MAX_BACKDROP_FILTERS = 2;
/** blur() and drop-shadow() radii, CSS px. */
export const PAGE_MAX_BLUR_PX = 24;
export const PAGE_MAX_BOX_SHADOW_BLUR_PX = 48;
export const PAGE_MAX_SMIL = 16;
export const PAGE_MAX_CSS_ANIMATIONS = 16;
export const PAGE_MAX_KEYFRAMES = 16;
export const PAGE_MIN_INFINITE_ANIMATION_S = 0.5;
export const PAGE_MAX_CSS_RULES = 800;
export const PAGE_MAX_STD_DEVIATION = 24;
export const PAGE_MAX_OCTAVES = 4;
export const PAGE_MAX_MORPHOLOGY_RADIUS = 8;
export const PAGE_MAX_CONVOLVE_ORDER = 5;
export const PAGE_MAX_GRADIENT_STOPS = 64;
// Rendered caps (contract §9.6).
export const PAGE_MAX_RENDERED_ANIMATIONS = 16;
export const PAGE_MAX_RENDERED_FILTERS = 12;
export const PAGE_MAX_RENDERED_BACKDROP_FILTERS = 2;
/** The media features a version 2 page may query, with their min- and max- forms. */
export const PAGE_MEDIA_FEATURES = Object.freeze([
  "width",
  "height",
  "aspect-ratio",
  "orientation",
  "prefers-reduced-motion",
]);

/** A live area's, a feed's, a group's and a fitted drawing's id: as a reading's name. */
const PART_ID = READING_NAME;
const FIELD_NAME = /^[a-z][a-z0-9-]{0,39}$/;
const SERIES_FIELD = /^[a-z][a-z0-9-]{0,31}$/;

function integerIn(text, min, max) {
  if (!/^(?:0|[1-9][0-9]{0,3})$/.test(text)) return false;
  const n = Number(text);
  return n >= min && n <= max;
}

function numbers(text, count) {
  const parts = text.trim().split(/[\s,]+/);
  if (parts.length !== count) return null;
  const out = parts.map((p) =>
    /^-?(?:\d+(?:\.\d+)?|\.\d+)$/.test(p) ? Number(p) : NaN,
  );
  return out.every(Number.isFinite) ? out : null;
}

/** True for a `data-tc-window` value: a window (pageWindow grammar) or a module feed's series, `feed:F.S`. */
function windowBindingAllowed(text) {
  const feed = /^feed:([a-z][a-z0-9-]{0,23})\.([a-z][a-z0-9-]{0,31})$/.exec(
    text,
  );
  if (feed !== null) return PART_ID.test(feed[1]) && SERIES_FIELD.test(feed[2]);
  return parsePageWindow(text) !== null;
}

const BINDING_RULES = {
  "data-tc-text": (v) => READING_NAME.test(v),
  "data-tc-unit": (v) => READING_NAME.test(v),
  "data-tc-age": (v) => READING_NAME.test(v),
  "data-tc-frac": (v) => READING_NAME.test(v),
  "data-tc-path": (v) => READING_NAME.test(v),
  "data-tc-window": windowBindingAllowed,
  "data-tc-feed": (v) => PART_ID.test(v),
  "data-tc-status": (v) => v === "mode" || v === "anchor",
  "data-tc-field": (v) => FIELD_NAME.test(v),
  "data-tc-slot": (v) => integerIn(v, 0, 47),
  "data-tc-decimals": (v) => integerIn(v, 0, 3),
  "data-tc-stat": (v) => ["last", "min", "max", "mean", "change"].includes(v),
  "data-tc-age-style": (v) => v === "readout",
  "data-tc-name": (v) => READING_NAME.test(v),
  "data-tc-equipment": (v) => READING_NAME.test(v),
  "data-tc-device": (v) => READING_NAME.test(v),
  "data-tc-state": (v) => READING_NAME.test(v),
  "data-tc-points": (v) => integerIn(v, 24, 400),
  "data-tc-shape": (v) => ["line", "area", "band"].includes(v),
  "data-tc-curve": (v) => ["monotone", "linear", "step"].includes(v),
  "data-tc-group": (v) => PART_ID.test(v),
  "data-tc-box": (v) => {
    const n = numbers(v, 4);
    return n !== null && n[2] > 0 && n[3] > 0;
  },
  "data-tc-fit": (v) => PART_ID.test(v),
  "data-tc-inset": (v) => {
    const n = numbers(v, 4);
    return n !== null && n.every((x) => x >= 0);
  },
  "data-tc-y": (v) => ["range", "auto", "feed"].includes(v),
  "data-tc-scale": (v) => PART_ID.test(v),
  "data-tc-tick": (v) => integerIn(v, 0, 7),
  "data-tc-time-tick": (v) => integerIn(v, 0, 7),
  "data-tc-window-text": (v) => v.trim() === "",
  "data-tc-mark": (v) => READING_NAME.test(v),
  "data-tc-at": (v) => ["last", "min", "max"].includes(v),
  "data-tc-day-band": (v) => integerIn(v, 0, 31),
  // The card's freshness line (TOM-989): it names nothing.
  "data-tc-freshness": (v) => v.trim() === "",
};

/** A chat page's own bindings (PAGE_CHAT_BINDING_ATTRIBUTES). */
const CHAT_BINDING_RULES = {
  "data-tc-result": (v) => PAGE_RESULT_ID.test(v),
};

/**
 * True for one binding's value (contract §4.2); false for a `data-tc-*` name
 * the vocabulary does not have. A chat page's bindings are in it only with
 * `{ results: true }` (cloud ADR 0399, TOM-870).
 */
export function pageBindingValueAllowed(name, value, options = {}) {
  const key = String(name).toLowerCase();
  const rule = Object.hasOwn(BINDING_RULES, key)
    ? BINDING_RULES[key]
    : options?.results === true && Object.hasOwn(CHAT_BINDING_RULES, key)
      ? CHAT_BINDING_RULES[key]
      : undefined;
  return rule !== undefined && rule(String(value));
}

/** True for a custom property the host sets on a page with these reading names. */
export function pageHostProperty(property, readingNames = []) {
  const name = String(property).trim().toLowerCase();
  if (PAGE_HOST_PROPERTIES.test(name)) return true;
  for (const reading of readingNames)
    if (name === `--${reading}` || name === `--${reading}-fraction`)
      return true;
  return false;
}

// ----- Windows (contract §4.5.1; the protocol's parseNeoPageWindow) ---------

const MINUTE = 60_000;
const HOUR = 3_600_000;
const DAY = 86_400_000;
const WINDOW_MIN_MS = 300_000;
const WINDOW_MAX_MS = 366 * DAY;
const ROLLING = /^([1-9][0-9]{0,5})(m|h|d)$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d{1,3})?)?Z$/;
const UNIT_MS = { m: MINUTE, h: HOUR, d: DAY };

/**
 * A window's span, or null (contract §4.5.1): rolling minutes, hours or days,
 * or fixed "<from>/<to>" UTC instants; 5 minutes to 366 days. The same
 * grammar as @neomaris/protocol's parseNeoPageWindow, checked over one corpus.
 */
export function parsePageWindow(text) {
  if (typeof text !== "string") return null;
  const rolling = ROLLING.exec(text);
  if (rolling !== null) {
    const ms = Number(rolling[1]) * UNIT_MS[rolling[2]];
    return ms >= WINDOW_MIN_MS && ms <= WINDOW_MAX_MS
      ? { kind: "rolling", ms }
      : null;
  }
  const parts = text.split("/");
  if (parts.length !== 2) return null;
  const [from, to] = parts;
  if (!INSTANT.test(from) || !INSTANT.test(to)) return null;
  const fromMs = Date.parse(from),
    toMs = Date.parse(to);
  if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return null;
  if (
    new Date(fromMs).toISOString().slice(0, 10) !== from.slice(0, 10) ||
    new Date(toMs).toISOString().slice(0, 10) !== to.slice(0, 10)
  )
    return null;
  const span = toMs - fromMs;
  return span > 0 && span >= WINDOW_MIN_MS && span <= WINDOW_MAX_MS
    ? { kind: "fixed", fromMs, toMs }
    : null;
}

/** [fromMs, toMs] for a window at `nowMs` (a rolling window ends now), or null. */
export function pageWindowBounds(window, nowMs) {
  const span = parsePageWindow(window);
  if (span === null) return null;
  return span.kind === "rolling"
    ? [nowMs - span.ms, nowMs]
    : [span.fromMs, span.toMs];
}

/** `${reading}|${window}|${points}`: how a page's series are keyed in its data. */
export function pageSeriesKey(reading, window, points) {
  return `${reading}|${window}|${points}`;
}

// ----- Calendar arithmetic, in a time zone or the process's own -------------

const zoneFormats = new Map();

function zoneFormat(timeZone) {
  let format = zoneFormats.get(timeZone);
  if (format === undefined) {
    format = new Intl.DateTimeFormat("en-US", {
      timeZone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      second: "numeric",
    });
    zoneFormats.set(timeZone, format);
  }
  return format;
}

/** The wall clock of an instant: in `timeZone`, or the process's own when it is undefined. */
function wall(t, timeZone) {
  if (timeZone === undefined) {
    const d = new Date(t);
    return {
      year: d.getFullYear(),
      month: d.getMonth(),
      day: d.getDate(),
      hour: d.getHours(),
      minute: d.getMinutes(),
    };
  }
  const parts = {};
  for (const { type, value } of zoneFormat(timeZone).formatToParts(t))
    parts[type] = Number(value);
  return {
    year: parts.year,
    month: parts.month - 1,
    day: parts.day,
    hour: parts.hour === 24 ? 0 : parts.hour,
    minute: parts.minute,
  };
}

/** The instant a wall clock reads, in `timeZone` or the process's own (month 0-based; days and hours may overflow). */
function instant(year, month, day, hour, minute, timeZone) {
  if (timeZone === undefined)
    return new Date(year, month, day, hour, minute, 0, 0).getTime();
  const guess = Date.UTC(year, month, day, hour, minute);
  let t = guess;
  // Two passes settle the zone's offset at the instant itself.
  for (let i = 0; i < 2; i += 1) {
    const w = wall(t, timeZone);
    const shown = Date.UTC(w.year, w.month, w.day, w.hour, w.minute);
    t = guess - (shown - t);
  }
  return t;
}

function startOfDay(t, timeZone) {
  const w = wall(t, timeZone);
  return instant(w.year, w.month, w.day, 0, 0, timeZone);
}

function addDays(dayStart, days, timeZone) {
  const w = wall(dayStart, timeZone);
  return instant(w.year, w.month, w.day + days, 0, 0, timeZone);
}

// ----- Ticks (HistoryChart's charts/lib/scales.ts, moved here) ---------------

function niceStep(span, target) {
  const raw = Math.abs(span) / Math.max(1, target);
  if (raw === 0 || !Number.isFinite(raw)) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const factor = norm < 1.5 ? 1 : norm < 3.5 ? 2 : norm < 7.5 ? 5 : 10;
  return factor * mag;
}

/** Round ticks covering [min, max]: clean 1/2/5 multiples (HistoryChart's linearTicks). */
export function pageLinearTicks(min, max, target = 4) {
  if (!Number.isFinite(min) || !Number.isFinite(max)) return [];
  if (min === max) return [min];
  const lo = Math.min(min, max);
  const hi = Math.max(min, max);
  const step = niceStep(hi - lo, target);
  const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
  const ticks = [];
  const first = Math.ceil(lo / step) * step;
  for (let v = first; v <= hi + step * 1e-6; v += step)
    ticks.push(Number(v.toFixed(decimals)));
  return ticks;
}

/** Every k-th tick, so neighbours sit at least `minGapPx` apart, zero kept (HistoryChart's spacedTicks). */
export function pageSpacedTicks(ticks, pxPerUnit, minGapPx) {
  if (ticks.length < 2 || !Number.isFinite(pxPerUnit)) return ticks;
  const gap = Math.abs(ticks[1] - ticks[0]) * Math.abs(pxPerUnit);
  if (gap >= minGapPx) return ticks;
  const k = gap > 0 ? Math.ceil(minGapPx / gap) : ticks.length;
  const zero = ticks.indexOf(0);
  const phase = zero === -1 ? 0 : zero % k;
  return ticks.filter((_, i) => i % k === phase);
}

/** A value domain padded for plotting, pinned to zero on request (HistoryChart's padDomain). */
export function pagePadDomain(min, max, frac = 0.08, opts = {}) {
  let lo = Math.min(min, max);
  let hi = Math.max(min, max);
  if (lo === hi) {
    const bump = Math.max(Math.abs(lo) * 0.1, 1);
    lo -= bump;
    hi += bump;
  }
  const pad = (hi - lo) * frac;
  lo -= pad;
  hi += pad;
  if (opts.zero === true) {
    if (lo > 0) lo = 0;
    if (hi < 0) hi = 0;
  }
  return [lo, hi];
}

const TIME_STEPS = [
  MINUTE,
  2 * MINUTE,
  5 * MINUTE,
  10 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  2 * HOUR,
  3 * HOUR,
  6 * HOUR,
  12 * HOUR,
  DAY,
  2 * DAY,
  7 * DAY,
  14 * DAY,
];

function alignUpSubDay(t, step, timeZone) {
  const w = wall(t, timeZone);
  let aligned;
  if (step < HOUR) {
    const stepMin = step / MINUTE;
    aligned = instant(
      w.year,
      w.month,
      w.day,
      w.hour,
      Math.floor(w.minute / stepMin) * stepMin,
      timeZone,
    );
  } else {
    const stepH = step / HOUR;
    aligned = instant(
      w.year,
      w.month,
      w.day,
      Math.floor(w.hour / stepH) * stepH,
      0,
      timeZone,
    );
  }
  while (aligned < t) aligned += step;
  return aligned;
}

function monthTicks(from, to, target, timeZone) {
  const w = wall(from, timeZone);
  let year = w.year,
    month = w.month;
  let t = instant(year, month, 1, 0, 0, timeZone);
  if (t < from) {
    month += 1;
    t = instant(year, month, 1, 0, 0, timeZone);
  }
  const out = [];
  while (t <= to) {
    out.push(t);
    month += 1;
    t = instant(year, month, 1, 0, 0, timeZone);
  }
  return out.length > target ? out.filter((_, i) => i % 2 === 0) : out;
}

/**
 * Time tick instants for [from, to], at most about `maxCount`, aligned to
 * natural boundaries in the zone (HistoryChart's timeTicks).
 */
export function pageTimeTickInstants(from, to, maxCount, timeZone) {
  if (!(to > from) || maxCount < 1) return [];
  const span = to - from;
  const target = Math.max(1, maxCount);
  let step = TIME_STEPS[TIME_STEPS.length - 1];
  for (const s of TIME_STEPS) {
    if (span / s <= target * 1.002) {
      step = s;
      break;
    }
  }
  if (span / step > target * 1.002)
    return monthTicks(from, to, target, timeZone);
  const ticks = [];
  if (step < DAY) {
    for (let t = alignUpSubDay(from, step, timeZone); t <= to; t += step)
      ticks.push(t);
    return ticks;
  }
  const days = Math.round(step / DAY);
  let d = startOfDay(from, timeZone);
  if (d < from) d = addDays(d, 1, timeZone);
  while (d <= to) {
    ticks.push(d);
    d = addDays(d, days, timeZone);
  }
  return ticks;
}

/** The zone's midnights inside [from, to] (HistoryChart's dayStarts). */
export function pageDayStarts(from, to, timeZone) {
  const out = [];
  let d = startOfDay(from, timeZone);
  while (d <= to) {
    out.push(d);
    d = addDays(d, 1, timeZone);
  }
  return out;
}

/** Rough text width in px (Inter at a size), as HistoryChart estimates it. */
export function pageTextWidth(text, fontSizePx) {
  return text.length * fontSizePx * 0.58;
}

/** Keep the tick labels that fit; drop every other one until they do (HistoryChart's thinLabels). */
export function pageThinLabels(ticks, labelFor, x, plotW) {
  let step = 1;
  const maxWidth = Math.max(
    ...ticks.map((t) => pageTextWidth(labelFor(t), 10.5)),
    1,
  );
  while (step < ticks.length && (ticks.length / step) * (maxWidth + 14) > plotW)
    step += 1;
  return ticks
    .filter((_, i) => i % step === 0)
    .map((t) => ({ t, label: labelFor(t) }));
}

function format(locale, timeZone, options) {
  return new Intl.DateTimeFormat(locale, {
    ...options,
    ...(timeZone !== undefined ? { timeZone } : {}),
  });
}

/** An axis tick's label for a window span: a clock inside two days, a date beyond (HistoryChart's formatTickTime). */
export function pageTickTimeText(t, spanMs, timeZone, locale) {
  if (spanMs <= 48 * HOUR)
    return format(locale, timeZone, {
      hour: "numeric",
      minute: "2-digit",
    }).format(t);
  if (spanMs <= 14 * DAY)
    return format(locale, timeZone, {
      weekday: "short",
      day: "numeric",
    }).format(t);
  return format(locale, timeZone, { month: "short", day: "numeric" }).format(t);
}

/**
 * A group's value ticks (contract §4.6), in the units `lo` and `hi` are in:
 * in a fitted drawing HistoryChart's, spaced 16 px apart at `plotHeightPx`;
 * outside one, four round ticks.
 */
export function pageValueTicks(lo, hi, plotHeightPx) {
  const ticks = pageLinearTicks(lo, hi, 4);
  if (plotHeightPx === null || plotHeightPx === undefined) return ticks;
  return pageSpacedTicks(ticks, plotHeightPx / (hi - lo), 16);
}

/**
 * A second value scale whose ticks sit on a first scale's (contract §4.6,
 * TOM-953), so a right axis's numbers fall on the left axis's grid lines.
 * `ticks` are the first scale's ticks and `[lo, hi]` its domain, in its own
 * units; `[lo2, hi2]` is the range of the second's data, in the second's.
 * The second's step is the smallest round one (1, 2 or 5 times a power of
 * ten, as pageLinearTicks steps) whose ticks, as many as the first's and
 * starting at the step's multiple at or below `lo2`, reach `hi2`; its tick j
 * sits where the first's tick j sits. Returns those ticks and the second's
 * domain over the whole plot, `{ ticks, lo, hi }`, or null when the first
 * has fewer than two ticks, a range is empty or a value is not finite.
 */
export function pageAlignedTicks(ticks, lo, hi, lo2, hi2) {
  const n = Array.isArray(ticks) ? ticks.length : 0;
  if (n < 2 || !ticks.every(numeric)) return null;
  if (![lo, hi, lo2, hi2].every(numeric) || !(hi > lo) || !(hi2 > lo2))
    return null;
  // The first's ticks are evenly spaced (pageSpacedTicks keeps every k-th).
  const f0 = (ticks[0] - lo) / (hi - lo);
  const df = (ticks[1] - ticks[0]) / (hi - lo);
  if (!(df > 0) || !numeric(f0)) return null;
  const need = (hi2 - lo2) / (n - 1);
  let mag = Math.pow(10, Math.floor(Math.log10(need)));
  for (let tries = 0; tries < 8 && numeric(mag) && mag > 0; tries += 1) {
    for (const factor of [1, 2, 5]) {
      const step = factor * mag;
      if (step < need * (1 - 1e-9)) continue;
      const first = Math.floor(lo2 / step + 1e-9) * step;
      if (first + (n - 1) * step < hi2 - step * 1e-9) continue;
      const decimals = Math.max(0, -Math.floor(Math.log10(step)) + 1);
      const out = [];
      for (let j = 0; j < n; j += 1)
        out.push(Number((first + j * step).toFixed(decimals)));
      const unit = step / df;
      const from = first - f0 * unit;
      return numeric(from) && numeric(unit) && unit > 0
        ? { ticks: out, lo: from, hi: from + unit }
        : null;
    }
    mag *= 10;
  }
  return null;
}

/**
 * A window's time ticks (contract §4.6): each tick's instant, its label in the
 * viewer's zone and its place, 0 at the window's start to 1 at its end. In a
 * fitted drawing as many as HistoryChart draws at `plotWidthPx`, thinned as it
 * thins them; outside one, five.
 */
export function pageTimeTicks(
  fromMs,
  toMs,
  plotWidthPx,
  timeZone,
  locale,
  maxTicks = Infinity,
) {
  if (!(toMs > fromMs)) return [];
  const span = toMs - fromMs;
  const fitted = plotWidthPx !== null && plotWidthPx !== undefined;
  // A drawing with fewer tick slots than its width holds asks for a coarser
  // step that still spans the axis (TOM-1001), never its first ticks only.
  const room = !fitted
    ? 5
    : span > 48 * HOUR
      ? Math.min(
          Math.max(2, Math.floor(plotWidthPx / 76)),
          Math.round(span / DAY),
        )
      : Math.max(2, Math.floor(plotWidthPx / 76));
  let count = Math.max(2, Math.min(room, maxTicks));
  let instants = pageTimeTickInstants(fromMs, toMs, count, timeZone);
  // A round step can give a tick more than asked: step coarser until they fit.
  while (instants.length > maxTicks && count > 2) {
    count -= 1;
    instants = pageTimeTickInstants(fromMs, toMs, count, timeZone);
  }
  while (instants.length > maxTicks)
    instants = instants.filter((_, i) => i % 2 === 0);
  const labelFor = (t) => pageTickTimeText(t, span, timeZone, locale);
  const kept = fitted
    ? pageThinLabels(
        instants,
        labelFor,
        (t) => ((t - fromMs) / span) * plotWidthPx,
        plotWidthPx,
      )
    : instants.map((t) => ({ t, label: labelFor(t) }));
  return kept.map(({ t, label }) => ({ t, label, frac: (t - fromMs) / span }));
}

/**
 * The local days a window touches (contract §4.6.1), for a window of at least
 * 40 hours, as HistoryChart bands days: each day's start and end as fractions
 * of the window, clamped, and its label (the day's short weekday). Empty for a
 * shorter window.
 */
export function pageDayBands(fromMs, toMs, timeZone, locale) {
  const span = toMs - fromMs;
  if (!(span >= 40 * HOUR)) return [];
  const starts = pageDayStarts(fromMs - DAY, toMs, timeZone);
  const bands = [];
  for (let i = 0; i < starts.length; i += 1) {
    const start = starts[i];
    const end = starts[i + 1] ?? toMs;
    const x0 = Math.min(1, Math.max(0, (start - fromMs) / span));
    const x1 = Math.min(1, Math.max(0, (end - fromMs) / span));
    if (x1 <= x0) continue;
    bands.push({
      startMs: start,
      x0,
      x1,
      label: format(locale, timeZone, { weekday: "short" }).format(
        start + 12 * HOUR,
      ),
    });
  }
  return bands;
}

/**
 * The window's label (contract §4.6), as the chart writes its window today
 * (the web's neoChartWindowLabel): "last 24 h" and "last 7 days" for a
 * rolling window, else its dates in the viewer's locale and zone, a window of
 * whole local days named by its days.
 */
export function pageWindowText(window, nowMs, locale, timeZone) {
  const span = parsePageWindow(window);
  if (span === null) return "";
  if (span.kind === "rolling") {
    if (span.ms < 48 * HOUR)
      return `last ${Math.max(1, Math.round(span.ms / HOUR))} h`;
    return `last ${Math.round(span.ms / DAY)} days`;
  }
  const { fromMs: from, toMs: to } = span;
  const days = format(locale, timeZone, { month: "short", day: "numeric" });
  const midnight = (t) => {
    const w = wall(t, timeZone);
    return w.hour === 0 && w.minute === 0 && t % MINUTE === 0;
  };
  if (midnight(from) && midnight(to)) {
    const first = days.format(from);
    const last = days.format(to - 1);
    return first === last ? first : `${first} – ${last}`;
  }
  if (to - from >= 2 * DAY) return `${days.format(from)} – ${days.format(to)}`;
  const f = format(locale, timeZone, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${f.format(from)} – ${f.format(to)}`;
}

// ----- Series: paths, scales, marks and statistics (contract §4.5, §4.6.1, §4.7)

/** A series' slots with their times: each slot at the middle of its span, clamped into [from, to]. */
function slotsOf(series, from = series.fromMs, to = series.toMs) {
  const b = series.bucketMs;
  if (!(b > 0)) return [];
  const start = Math.floor(series.fromMs / b) * b;
  return series.slots.map((slot, i) => ({
    t: Math.min(to, Math.max(from, start + i * b + b / 2)),
    slot,
  }));
}

function slotValue(slot, which) {
  if (slot === null || slot === undefined) return null;
  const v =
    which === "min"
      ? (slot[1] ?? slot[0])
      : which === "max"
        ? (slot[2] ?? slot[0])
        : slot[0];
  return numeric(v) ? v : null;
}

/**
 * A group's value scale (contract §4.5.3), `data-tc-y="auto"`: over every
 * series of the group, the lowest and highest values the paths draw (a band's
 * slot minima and maxima, a line's or an area's averages), padded as
 * HistoryChart pads its domain. Null when the series hold no value.
 * `seriesList` is `[{ series, shape }]`; `options` `{ pad, zero }`.
 */
export function pageYRange(seriesList, options = {}) {
  let lo = Infinity,
    hi = -Infinity;
  for (const { series, shape } of seriesList) {
    if (!series) continue;
    for (const slot of series.slots) {
      const low = slotValue(slot, shape === "band" ? "min" : "avg");
      const high = slotValue(slot, shape === "band" ? "max" : "avg");
      if (low !== null && low < lo) lo = low;
      if (high !== null && high > hi) hi = high;
    }
  }
  if (!(lo <= hi)) return null;
  return pagePadDomain(lo, hi, options.pad ?? 0.09, { zero: options.zero });
}

const fmt = (n) => (Math.round(n * 100) / 100).toString();
const sign = (n) => (n < 0 ? -1 : 1);

function slope3(x0, y0, x1, y1, x2, y2) {
  const h0 = x1 - x0;
  const h1 = x2 - x1;
  const s0 = (y1 - y0) / (h0 !== 0 ? h0 : h1 < 0 ? -0 : 0);
  const s1 = (y2 - y1) / (h1 !== 0 ? h1 : h0 < 0 ? -0 : 0);
  const p = (s0 * h1 + s1 * h0) / (h0 + h1);
  const m =
    (sign(s0) + sign(s1)) *
    Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p));
  return Number.isNaN(m) ? 0 : m;
}

function slope2(x0, y0, x1, y1, t) {
  const h = x1 - x0;
  return h !== 0 ? (3 * (y1 - y0)) / h - t : t;
}

/** The charts' curve.ts, moved: a monotone cubic, straight segments, or steps. */
function segmentsFor(pts, curve) {
  if (curve === "step") {
    let d = "";
    for (let i = 1; i < pts.length; i += 1)
      d += `L${fmt(pts[i].x)},${fmt(pts[i - 1].y)}L${fmt(pts[i].x)},${fmt(pts[i].y)}`;
    return d;
  }
  if (curve === "linear")
    return pts
      .slice(1)
      .map((p) => `L${fmt(p.x)},${fmt(p.y)}`)
      .join("");
  const n = pts.length;
  if (n < 2) return "";
  if (n === 2) return `L${fmt(pts[1].x)},${fmt(pts[1].y)}`;
  let d = "";
  let t0 = 0;
  for (let i = 0; i < n - 1; i += 1) {
    const p0 = pts[i];
    const p1 = pts[i + 1];
    let t1;
    if (i + 2 < n) {
      const p2 = pts[i + 2];
      t1 = slope3(p0.x, p0.y, p1.x, p1.y, p2.x, p2.y);
    } else {
      t1 = slope2(p0.x, p0.y, p1.x, p1.y, t0);
    }
    if (i === 0) t0 = slope2(p0.x, p0.y, p1.x, p1.y, t1);
    const dx = (p1.x - p0.x) / 3;
    d +=
      `C${fmt(p0.x + dx)},${fmt(p0.y + dx * t0)}` +
      ` ${fmt(p1.x - dx)},${fmt(p1.y - dx * t1)}` +
      ` ${fmt(p1.x)},${fmt(p1.y)}`;
    t0 = t1;
  }
  return d;
}

function linePath(pts, curve) {
  if (pts.length === 0) return "";
  const first = pts[0];
  // One slot of data draws as a zero-length segment, a dot under a round cap.
  if (pts.length === 1)
    return `M${fmt(first.x)},${fmt(first.y)}L${fmt(first.x)},${fmt(first.y)}`;
  return `M${fmt(first.x)},${fmt(first.y)}${segmentsFor(pts, curve)}`;
}

/**
 * A series as one path's `d` (contract §4.5.2 to §4.5.4). `options`: `box`
 * `{ x, y, w, h }` in the svg's units; `scale` `[lo, hi]` in store units;
 * `shape` line, area or band; `curve` monotone, linear or step; `from` and
 * `to`, the window's ends (the series' own by default). A gap ends a segment
 * and is never bridged; an angle draws its line only, broken where two slots
 * lie more than half the scale apart. "" with no data.
 */
export function pageSeriesPath(series, options) {
  if (!series) return "";
  const { box, scale } = options;
  const shape = series.angle ? "line" : (options.shape ?? "line");
  const curve =
    options.curve === "linear" || options.curve === "step"
      ? options.curve
      : "monotone";
  const from = options.from ?? series.fromMs;
  const to = options.to ?? series.toMs;
  const [lo, hi] = scale;
  if (!(to > from) || !(hi !== lo)) return "";
  const x = (t) => box.x + (box.w * (t - from)) / (to - from);
  const y = (v) => box.y + box.h * (1 - (v - lo) / (hi - lo));
  const half = Math.abs(hi - lo) / 2;
  const segments = [];
  let run = [];
  let previous = null;
  for (const { t, slot } of slotsOf(series, from, to)) {
    const v = slotValue(slot, "avg");
    if (v === null) {
      if (run.length > 0) segments.push(run);
      run = [];
      previous = null;
      continue;
    }
    if (series.angle && previous !== null && Math.abs(v - previous) > half) {
      segments.push(run);
      run = [];
    }
    run.push({ t, slot, v });
    previous = v;
  }
  if (run.length > 0) segments.push(run);
  let d = "";
  const bottom = box.y + box.h;
  for (const seg of segments) {
    const pts = seg.map((p) => ({ x: x(p.t), y: y(p.v) }));
    if (shape === "line") {
      d += linePath(pts, curve);
    } else if (shape === "area") {
      const first = pts[0];
      const last = pts[pts.length - 1];
      d += `${linePath(pts, curve)}L${fmt(last.x)},${fmt(bottom)}L${fmt(first.x)},${fmt(bottom)}Z`;
    } else {
      const top = seg.map((p) => ({
        x: x(p.t),
        y: y(slotValue(p.slot, "max")),
      }));
      const back = seg
        .map((p) => ({ x: x(p.t), y: y(slotValue(p.slot, "min")) }))
        .reverse();
      d += `${linePath(top, curve)}L${fmt(back[0].x)},${fmt(back[0].y)}${segmentsFor(back, curve)}Z`;
    }
  }
  return d;
}

/**
 * One point of a series (contract §4.6.1): `last`, the last slot with data;
 * `min`, the slot holding the lowest minimum; `max`, the highest maximum. Its
 * place as fractions of the window (x, 0 at the left) and of `scale` (y, 0 at
 * the bottom), and its value in store units; null with no point.
 */
export function pageSeriesMark(series, at, scale) {
  if (!series) return null;
  let best = null;
  for (const { t, slot } of slotsOf(series)) {
    const v = slotValue(slot, at === "last" ? "avg" : at);
    if (v === null) continue;
    if (
      best === null ||
      at === "last" ||
      (at === "min" && v < best.v) ||
      (at === "max" && v > best.v)
    )
      best = { t, v };
  }
  if (best === null) return null;
  const span = series.toMs - series.fromMs;
  const [lo, hi] = scale;
  return {
    x: span > 0 ? Math.min(1, Math.max(0, (best.t - series.fromMs) / span)) : 0,
    y: hi !== lo ? Math.min(1, Math.max(0, (best.v - lo) / (hi - lo))) : 0.5,
    value: best.v,
    atMs: best.t,
  };
}

/**
 * A statistic over a series (contract §4.7), in store units: `min` the lowest
 * slot minimum, `max` the highest maximum, `mean` the averages weighted by
 * their counts, `last` the last slot with data's average; `change` gives the
 * first and the last slot's averages, `{ first, last }`, which the host
 * converts each before subtracting. Null with no data.
 */
export function pageSeriesStat(series, stat) {
  if (!series) return null;
  const withData = series.slots.filter((s) => slotValue(s, "avg") !== null);
  if (withData.length === 0) return null;
  if (stat === "last") return slotValue(withData[withData.length - 1], "avg");
  if (stat === "change")
    return {
      first: slotValue(withData[0], "avg"),
      last: slotValue(withData[withData.length - 1], "avg"),
    };
  if (stat === "min")
    return Math.min(...withData.map((s) => slotValue(s, "min")));
  if (stat === "max")
    return Math.max(...withData.map((s) => slotValue(s, "max")));
  if (stat === "mean") {
    let sum = 0,
      count = 0;
    for (const s of withData) {
      const n = numeric(s[3]) && s[3] > 0 ? s[3] : 1;
      sum += slotValue(s, "avg") * n;
      count += n;
    }
    return sum / count;
  }
  return null;
}

/**
 * A reading's name on a page, from its path (the cloud's panel-spec.ts
 * pageReadingName, moved here): the path after its group, in lowercase words
 * joined by dashes, at most 24 characters, starting with a letter, numbered
 * ("-2") when `taken` already has it.
 */
export function pageReadingNameFor(path, taken = new Set()) {
  const has = (name) =>
    typeof taken.has === "function" ? taken.has(name) : false;
  const segments = String(path)
    .split(".")
    .filter((p) => p.length > 0);
  const words = (segments.length > 1 ? segments.slice(1) : segments)
    .map((p) => p.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase())
    .join("-")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  let base = /^[a-z]/.test(words)
    ? words
    : `reading${words.length > 0 ? `-${words}` : ""}`;
  base = base.slice(0, 24).replace(/-+$/, "");
  if (!READING_NAME.test(base)) base = "reading";
  if (!has(base)) return base;
  for (let n = 2; ; n += 1) {
    const suffix = `-${n}`;
    const name = `${base.slice(0, 24 - suffix.length).replace(/-+$/, "")}${suffix}`;
    if (!has(name)) return name;
  }
}

// ----- The CSS a version 2 page may not hold (contract §9.2) ------------------

const VIEWPORT_HEIGHT =
  /(?:^|[^a-z0-9_-])-?(?:\d+(?:\.\d+)?|\.\d+)(?:[sld]?vh|[sld]?vmin|[sld]?vmax)\b/;

/**
 * The CSS refusals of contract §9.2 a regular pass can see, each a sentence:
 * the drawer's second lock behind the server's full parse. A page whose CSS
 * gives any is inserted with none of its CSS. `readingNames` adds each
 * reading's own host-set properties.
 */
export function pageCssV2Refusals(css, readingNames = []) {
  const refusals = [];
  const add = (sentence) => {
    if (!refusals.includes(sentence)) refusals.push(sentence);
  };
  if (!cssLooksSafe(css))
    add(
      "the CSS loads something from outside the page, or uses a rule a page may not",
    );
  if (!cssUrlsAreFragments(css))
    add(
      "a page holds no picture: url() may point only at a #fragment of the page",
    );
  for (const form of [normalizeCSS(css), normalizeCSS(css, true)]) {
    for (const [, rule] of form.matchAll(/@([a-z0-9_-]*)/g)) {
      if (rule === "font-face")
        add("a page writes with the theme's fonts: @font-face is not allowed");
      if (rule === "supports") add("@supports is not allowed");
    }
    for (const [, query] of form.matchAll(/@media([^{]*)\{/g)) {
      for (const [, feature] of query.matchAll(/\(\s*([a-z-]+)\s*[:)]/g)) {
        const base = feature.replace(/^(?:min|max)-/, "");
        if (!PAGE_MEDIA_FEATURES.includes(base))
          add(
            `@media may ask only for ${PAGE_MEDIA_FEATURES.join(", ")}: not ${feature}`,
          );
      }
      if (/prefers-color-scheme/.test(query))
        add("the theme is [data-theme], never prefers-color-scheme");
    }
    if (/(?:^|[;{\s])(?:direction|unicode-bidi|writing-mode)\s*:/.test(form))
      add("a page may not change the direction text is written in");
    if (/::?first-(?:letter|line)/.test(form))
      add("::first-letter and ::first-line are not allowed");
    for (const [, value] of form.matchAll(/letter-spacing\s*:\s*([^;}]*)/g))
      if (/^\s*-/.test(value)) add("letter-spacing may not be below 0");
    if (/(?:^|[;{\s])d\s*:/.test(form))
      add("the d of a path is the app's: the CSS d property is not allowed");
    for (const [, value] of form.matchAll(/content\s*:\s*([^;}]*)/g))
      if (/(?:counters?|attr)\s*\(/.test(value))
        add("content may not use counter(), counters() or attr()");
    for (const [, value] of form.matchAll(
      /counter-(?:reset|set|increment)\s*:\s*([^;}]*)/g,
    ))
      if (/\d/.test(value))
        add(
          "counter-reset, counter-set and counter-increment may not set a number",
        );
    if (VIEWPORT_HEIGHT.test(form))
      add("viewport height units (vh, vmin, vmax) are not allowed");
    if (/animation-play-state\s*:/.test(form))
      add("animation-play-state is the app's, for reduced motion");
    for (const [, property, value] of form.matchAll(
      /((?:animation|transition)[a-z-]*)\s*:\s*([^;}]*)/g,
    ))
      if (/!\s*important/.test(value))
        add(`!important is not allowed on ${property}`);
    if (/:has\s*\(/.test(form)) add("the :has() selector is not allowed");
    for (const [, property] of form.matchAll(
      /(?:^|[;{\s])(--[a-z0-9_-]+)\s*:/g,
    ))
      if (pageHostProperty(property, readingNames))
        add(`${property} is set by the app, never by the page`);
    for (const [, property] of form.matchAll(/@property\s+(--[a-z0-9_-]+)/g))
      if (pageHostProperty(property, readingNames))
        add(`${property} is set by the app, never registered by the page`);
    const keyframes = [...form.matchAll(/@keyframes\b/g)].length;
    if (keyframes > PAGE_MAX_KEYFRAMES)
      add(`at most ${PAGE_MAX_KEYFRAMES} @keyframes`);
    const rules = [...form.matchAll(/\{/g)].length;
    if (rules > PAGE_MAX_CSS_RULES)
      add(`at most ${PAGE_MAX_CSS_RULES} CSS rules`);
  }
  return refusals;
}
